<script>
import { didBackendFilterChange } from '@utils/filters'
import FilterDates from '@modules/global/components/filter-dates.vue'

/**
 * BaseFilterMenu — botão "Filtros" (ícone + contagem) + popover (`v-menu`) com os
 * campos definidos em `filters` e o rodapé Cancelar/Aplicar. Reusável fora do
 * BasePageHeader (é ele quem injeta este componente no slot `filter` por padrão).
 *
 * Os filtros só passam a valer ao clicar "Aplicar" OU ao fechar o popover
 * clicando fora dele (o popover edita um `draft`); campos `applyOnChange`
 * (ex: busca) aplicam na hora, com debounce opcional. "Limpar" só reseta os
 * campos visíveis (draft) — não aplica/recarrega sozinho.
 *
 * `filters` é um array de configuração (metadata estática) — mesmo formato que o
 * BasePageHeader documenta. As options dinâmicas de selects são injetadas pelo
 * pai (cada campo já vem com `options`/`data` resolvidas).
 *
 * Campo com `syncFromDefault: true` é de propriedade do PAI: o `applied` dele é
 * re-semeado a partir do `default` sempre que `filters` muda, em vez de preservar
 * o valor local. Use quando o mesmo filtro também tem um controle FORA do popover
 * (ex: o toggle "Somente eu" do histórico de transcrições, que escreve o mesmo
 * `id_profissional`) — sem isso o popover ignoraria o que o controle externo fez.
 * Não se aplica a `type:'date-range'`, que não tem `key` (usa `startKey`/`endKey`):
 * ali a flag é ignorada — semeie por `defaultStart`/`defaultEnd`.
 *
 * Campos `type:'slot'` custom (ex: o multi-select "Portfólio" do wallet-filters)
 * são responsabilidade de quem os declara — se tiverem seu próprio dropdown com
 * commit postergado (ex: select-search), quem escuta `@apply` aqui (com
 * `meta.live === false`) deve fechar/commitar esses campos ANTES de aplicar
 * (ver wallet-filters.vue `onApply` + ConsolidatorWalletFilter.blurOpenFields()).
 *
 * Props: `filters`, `filterButtonLabel`, `menuTitle`, `filterIcon`, `menuWidth`.
 * Slots:
 *  - `field-<key>` — controle custom de campo `type:'slot'` (recebe `{ field, draft }`).
 *  - `filter-button` — activator custom do menu (recebe `{ on, attrs, count }`).
 * Emits:
 *  - `apply (flat, meta)` — `meta.live` para aplicações em tempo real (busca).
 *  - `clear` — disparado quando o usuário usa "Limpar" (sem aplicar sozinho).
 */
// Largura mínima do popover (px) — garante que o rodapé (Cancelar + Aplicar)
// nunca estoure/corte, independente do `menuWidth` informado.
const MIN_MENU_WIDTH = 360

export default {
  name: 'BaseFilterMenu',

  components: { FilterDates },

  props: {
    filters: {
      type: Array,
      default: () => [],
    },
    filterButtonLabel: {
      type: String,
      default: 'Filtros',
    },
    // Título exibido no topo do popover de filtros.
    menuTitle: {
      type: String,
      default: 'Filtros da página',
    },
    filterIcon: {
      type: String,
      default: 'filter-new-ux',
    },
    activeCountOverride: {
      type: Number,
      default: null,
    },
    /**
     * Largura do menu de filtros (em px). É respeitado um piso de
     * MIN_MENU_WIDTH para o rodapé nunca cortar/estourar.
     */
    menuWidth: {
      type: [String, Number],
      default: 360,
    },
  },

  data() {
    return {
      menu: false,
      draft: {},
      applied: {},
    }
  },

  computed: {
    resolvedMenuWidth() {
      const width = Number(this.menuWidth) || MIN_MENU_WIDTH
      return Math.max(width, MIN_MENU_WIDTH)
    },

    hasFilters() {
      return Array.isArray(this.filters) && this.filters.length > 0
    },

    hasDraftChanges() {
      return didBackendFilterChange({
        keys: Object.keys(this.draft),
        prev: this.applied,
        next: this.draft,
      })
    },

    activeCount() {
      if (this.activeCountOverride !== null) return this.activeCountOverride
      let count = 0

      for (const field of this.filters) {
        if (field.type === 'date-range') {
          const start = this.applied[field.startKey]
          const end = this.applied[field.endKey]

          if (this.hasValue(start) || this.hasValue(end)) {
            count++
          }
        } else if (this.hasValue(this.applied[field.key])) {
          count++
        }
      }

      return count
    },
  },

  watch: {
    menu(open) {
      if (open) {
        this.$emit('open')
        this.draft = this.clone(this.applied)
        return
      }
      // Fechou sem passar por "Aplicar"/"Cancelar" (ex: clicou fora do
      // popover) — trata como uma aplicação implícita do que estava em
      // edição, igual pedido: só recarrega ao Aplicar ou ao fechar clicando
      // fora do menu.
      if (this.suppressCloseApply) {
        this.suppressCloseApply = false
        return
      }
      this.commitDraftAsApplied()
    },

    filters: {
      // Reconcilia (preserva valores ja preenchidos) em vez de zerar — senao um
      // update async da store (que recomputa o array `filters` p/ re-resolver
      // options) zeraria applied/draft, perdendo a selecao em andamento com o
      // menu aberto (condicao de corrida).
      handler() {
        this.reconcileState()
      },
      deep: true,
    },
  },

  created() {
    // Timers de debounce por campo (instance state — nunca module-scope).
    this.debounceTimers = {}
    // Evita que fechar o popover programaticamente via applyFilters()/
    // cancelFilters() (que já tratam o apply/discard) dispare de novo o apply
    // implícito do watch de `menu` acima.
    this.suppressCloseApply = false
    this.initState()
    this.$emit('apply', this.clone(this.applied))
  },

  beforeDestroy() {
    this.clearDebounceTimers()
  },

  methods: {
    clone(obj) {
      return structuredClone(obj || {})
    },

    hasValue(value) {
      if (value === null || value === undefined || value === '') {
        return false
      }

      // Boolean desligado (ex: toggle "favoritos") não conta como filtro ativo.
      if (value === false) {
        return false
      }

      if (Array.isArray(value) && value.length === 0) {
        return false
      }

      return true
    },

    buildInitialState() {
      const state = {}

      for (const field of this.filters) {
        if (field.type === 'date-range') {
          state[field.startKey] =
            field.defaultStart === undefined ? null : field.defaultStart
          state[field.endKey] =
            field.defaultEnd === undefined ? null : field.defaultEnd
        } else {
          state[field.key] = field.default === undefined ? null : field.default
        }
      }

      return state
    },

    // Estado "como o filtro nasce" (born-default) — usado pelo "Limpar".
    // Usa o born-default declarado no campo (`clearTo` / `clearStart`/`clearEnd`),
    // com fallback vazio (null). NÃO usa o `default` (que o pai injeta com o
    // valor APLICADO), senão limpar viraria no-op. Sem born-default declarado,
    // limpar zera o campo (comportamento padrão).
    buildClearState() {
      const state = {}

      for (const field of this.filters) {
        if (field.type === 'date-range') {
          state[field.startKey] =
            field.clearStart === undefined ? null : field.clearStart
          state[field.endKey] =
            field.clearEnd === undefined ? null : field.clearEnd
        } else {
          state[field.key] = field.clearTo === undefined ? null : field.clearTo
        }
      }

      return state
    },

    initState() {
      const initial = this.buildInitialState()
      this.applied = this.clone(initial)
      this.draft = this.clone(initial)
    },

    // Reconcilia applied/draft com a config ATUAL de `filters` SEM descartar o
    // que ja foi preenchido: preserva o valor existente de cada campo que
    // continua existindo, semeia apenas campos NOVOS (com o default do campo) e
    // descarta campos que sumiram. Chamado pelo watcher de `filters` quando a
    // store atualiza as options async (o array e recomputado) — evita perda
    // silenciosa de filtro/selecao (inclusive draft com o menu aberto).
    reconcileState() {
      const initial = this.buildInitialState()
      this.applied = this.reconcile(
        this.applied,
        initial,
        this.parentOwnedKeys()
      )
      // `draft` fica de fora do override de propósito: reconcileState roda também
      // quando as options chegam async, e sobrescrevê-lo mataria a seleção em
      // edição. Ele recebe o valor no watcher de `menu` (draft = clone(applied)).
      this.draft = this.reconcile(this.draft, initial)
    },

    // Chaves dos campos marcados com `syncFromDefault` (ver doc no topo).
    parentOwnedKeys() {
      return this.filters
        .filter((field) => field.syncFromDefault && field.key)
        .map((field) => field.key)
    },

    reconcile(current, initial, overrideKeys = []) {
      const source = current || {}
      const next = {}
      for (const key of Object.keys(initial)) {
        const preserve = key in source && !overrideKeys.includes(key)
        next[key] = preserve ? source[key] : initial[key]
      }
      return next
    },

    resolveOptions(field) {
      if (field.options) {
        return field.options
      }

      if (field.data) {
        return field.data
      }

      return []
    },

    // Aplica o estado atual como filtro "live" (sem fechar o menu). Usado pelos
    // campos `applyOnChange` (ex: busca).
    emitLiveApply() {
      this.applied = this.clone(this.draft)
      this.$emit('apply', this.clone(this.applied), { live: true })
    },

    // Agenda um emitLiveApply após `delay` ms, cancelando o anterior do mesmo
    // campo (debounce). Evita filtrar a cada tecla em campos de busca.
    scheduleLiveApply(key, delay) {
      if (this.debounceTimers[key]) {
        clearTimeout(this.debounceTimers[key])
      }
      this.debounceTimers[key] = setTimeout(() => {
        this.debounceTimers[key] = null
        this.emitLiveApply()
      }, delay)
    },

    clearDebounceTimers() {
      for (const key in this.debounceTimers) {
        if (this.debounceTimers[key]) {
          clearTimeout(this.debounceTimers[key])
          this.debounceTimers[key] = null
        }
      }
    },

    // Atualiza o draft do campo. Se o campo for `applyOnChange` (ex: busca),
    // aplica na hora SEM fechar o menu — com debounce opcional (`field.debounce`
    // em ms) para não filtrar a cada tecla.
    onFieldInput(field, value) {
      this.draft[field.key] = value

      if (!field.applyOnChange) return

      if (field.debounce) {
        this.scheduleLiveApply(field.key, field.debounce)
      } else {
        this.emitLiveApply()
      }
    },

    commitDraftAsApplied() {
      this.clearDebounceTimers()
      const changed = this.hasDraftChanges
      this.applied = this.clone(this.draft)
      if (!changed) return
      this.$emit('apply', this.clone(this.applied), { live: false })
    },

    applyFilters() {
      this.suppressCloseApply = true
      this.commitDraftAsApplied()
      this.menu = false
    },

    // Link "Limpar" do header: só reseta os campos visíveis (draft) —
    // NÃO aplica nem recarrega sozinho. A aplicação real ocorre ao clicar
    // "Aplicar" ou ao fechar o popover clicando fora dele (ver watch de
    // `menu`), igual qualquer outra edição de filtro em andamento.
    clearFilters() {
      if (this.activeCount === 0) return

      this.clearDebounceTimers()
      const cleared = this.buildClearState()
      this.draft = this.clone(cleared)
      this.$emit('clear', this.clone(cleared))
    },

    // Botão "Cancelar" do rodapé: descarta a edição e fecha sem aplicar.
    cancelFilters() {
      this.suppressCloseApply = true
      this.clearDebounceTimers()
      this.draft = this.clone(this.applied)
      this.menu = false
    },
  },
}
</script>

<template>
  <v-menu
    v-if="hasFilters"
    v-model="menu"
    :close-on-content-click="false"
    offset-y
    left
    :nudge-bottom="8"
    transition="slide-y-transition"
    content-class="base-filter-menu__menu-content"
    :min-width="resolvedMenuWidth"
    :max-width="resolvedMenuWidth"
  >
    <template v-slot:activator="{ on, attrs }">
      <slot name="filter-button" :on="on" :attrs="attrs" :count="activeCount">
        <BaseButton bordered fitContent v-bind="attrs" v-on="on">
          <icon-svg :name="filterIcon" :size="18" color="#030309" />
          {{ filterButtonLabel }}
          <span v-if="activeCount > 0" class="az-ds-badge-count ml-1">
            {{ activeCount }}
          </span>
          <icon-svg
            name="mdi-chevron-down"
            :size="18"
            color="#8E8E8E"
            class="base-filter-menu__chevron"
            :class="{ 'base-filter-menu__chevron--open': menu }"
          />
        </BaseButton>
      </slot>
    </template>

    <div
      class="base-filter-menu__menu"
      :style="{ width: resolvedMenuWidth + 'px' }"
    >
      <div
        class="base-filter-menu__menu-header d-flex align-center justify-space-between"
      >
        <span class="az-text-primary az-size-14 font-medium">
          {{ menuTitle }}
        </span>
        <!-- Sem estado disabled visual: o link aparece sempre normal; quando
             não há filtros ativos, clearFilters() apenas não faz nada. -->
        <span
          class="az-text-email-link base-filter-menu__clear"
          @click="clearFilters"
        >
          Limpar
        </span>
      </div>

      <div class="base-filter-menu__menu-body">
        <v-row no-gutters>
          <v-col
            v-for="field in filters"
            :key="field.key || field.startKey"
            :cols="field.cols || 12"
            class="base-filter-menu__field"
            v-bind="field.col || {}"
          >
            <span v-if="field.label" class="az-text-label d-block">
              {{ field.label }}
            </span>

            <!-- Campo custom: a view fornece o controle via slot
                 `field-<key>` (ex: um seletor próprio como o de funil). -->
            <slot
              v-if="field.type === 'slot'"
              :name="'field-' + field.key"
              :field="field"
              :draft="draft"
            />

            <FilterDates
              v-else-if="field.type === 'date-range'"
              :start-date.sync="draft[field.startKey]"
              :end-date.sync="draft[field.endKey]"
              :width-fields="field.widthFields || 140"
              :vertical="field.vertical"
              use-label-above
              clearable
              v-bind="field.props || {}"
            />

            <input-by-type
              v-else
              :value="draft[field.key]"
              :type="field.type"
              :options="resolveOptions(field)"
              :key-id="field.value"
              :key-item="field.text"
              :placeholder="field.placeholder"
              :max-width="field.maxWidth || '100%'"
              v-bind="field.props || {}"
              @input="onFieldInput(field, $event)"
            />
          </v-col>
        </v-row>
      </div>

      <v-divider />

      <div
        class="base-filter-menu__menu-footer d-flex justify-end align-center"
      >
        <span
          v-if="$slots['footer-leading'] || $scopedSlots['footer-leading']"
          class="base-filter-menu__footer-leading mr-auto"
        >
          <slot name="footer-leading" />
        </span>
        <BaseButton
          underlined
          fitContent
          min-width="auto"
          @click="cancelFilters"
        >
          Cancelar
        </BaseButton>
        <BaseButton primary fitContent min-width="110px" @click="applyFilters">
          Aplicar
        </BaseButton>
      </div>
    </div>
  </v-menu>
</template>

<style lang="sass" scoped>
.base-filter-menu
  &__chevron
    transition: transform 0.15s ease

    &--open
      transform: rotate(180deg)

  // Badge de contagem redondo (círculo p/ 1 dígito, pill a partir de 2)
  &__menu
    background-color: #fff

  &__menu-header
    padding: 14px 16px 4px

  // "Limpar" um pouco maior e bold, com hover de hiperlink (sublinhado).
  &__clear
    font-size: 14px
    font-weight: 700
    padding: 4px 0
    cursor: pointer

    &:hover
      text-decoration: underline

    &:active
      opacity: 0.7

  &__menu-body
    padding: 16px
    max-height: calc(100vh - 260px)
    overflow-y: auto

  // Espaçamento vertical entre campos (a v-row usa no-gutters).
  &__field
    margin-bottom: 16px

    &:last-child
      margin-bottom: 0

  // Rodapé das ações com cor de fundo diferente (igual ao mock)
  &__menu-footer
    gap: 8px
    padding: 16px
    background-color: var(--neutral-50)
</style>

<style lang="sass">
// Arredondamento + borda + clip do popover (conteúdo é teleportado ao body, por
// isso fora do bloco scoped). Usa CSS vars do DS (sem @import: tokens globais).
.v-menu__content.base-filter-menu__menu-content
  border: 1px solid var(--border-hairline)
  border-radius: var(--radius-md)
  overflow: hidden
</style>
